import { useAuthorization } from '@frontend/authorization';
import { Logger } from '@frontend/logger';
import { getResolveFunction, useReactiveObject } from '@frontend/repository';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { PlatformDetailEntry, parsePlatformDetailStack, platformDetailKey } from '../platform-modal-host/platform-detail-url';
import { usePlatformModalHost } from '../platform-modal-host/platform-modal-host.context';
import { AnyReactiveDetailRegistryEntry, ReactiveDetailDropdownOption, ReactiveDetailTab, ReactiveDetailView } from './reactive-detail.types';

/* eslint-disable @typescript-eslint/no-explicit-any */
type AnyView = ReactiveDetailView<any, any>;
type AnyTab = ReactiveDetailTab<any, any>;
type AnyDropdownOption = ReactiveDetailDropdownOption<any, any>;
/* eslint-enable @typescript-eslint/no-explicit-any */

export type ReactiveDetailShellStatus = 'loading' | 'not-found' | 'error' | 'ready';

export interface ReactiveDetailShellViewProps {
    status: ReactiveDetailShellStatus;
    /** Set whenever the reactive object is available. */
    view: AnyView | null;
    tabs: AnyTab[];
    activeTab?: AnyTab;
    activeDropdownOption?: AnyDropdownOption;
    onChangeTab: (tabId: string) => void;
    onChangeDropdown: (tabId: string, optionId: string) => void;
}

const NO_IDS: Record<string, string> = {};

interface ExtendedState {
    loaded: boolean;
    failed: boolean;
    value: unknown;
}

function useExtendedDetail(
    registryEntry: AnyReactiveDetailRegistryEntry,
    object: { id: string } | null,
    parents: Record<string, string>,
    context: Record<string, string>
): ExtendedState {
    const [state, setState] = useState<ExtendedState>({ loaded: false, failed: false, value: undefined });
    const { fetchExtended, entityType } = registryEntry;

    useEffect(() => {
        if (!object || !fetchExtended) {
            return;
        }
        let cancelled = false;
        fetchExtended({ object, parents, context })
            .then((value) => {
                if (!cancelled) setState({ loaded: true, failed: false, value });
            })
            .catch((error) => {
                Logger.error('ReactiveDetailShell: fetchExtended failed', { entityType, id: object.id, error });
                if (!cancelled) setState((previous) => ({ ...previous, failed: true }));
            });
        return () => {
            cancelled = true;
        };
    }, [fetchExtended, entityType, object, parents, context]);

    return state;
}

/** Prefer the live URL entry so tab/dropdown updates reach panes opened with a stale render closure. */
function useLiveDetailEntry(detail: PlatformDetailEntry): PlatformDetailEntry {
    const location = useLocation();
    return useMemo(() => {
        const key = platformDetailKey(detail);
        return parsePlatformDetailStack(location.search).find((entry) => platformDetailKey(entry) === key) ?? detail;
    }, [detail, location.search]);
}

export function useReactiveDetailShell(detail: PlatformDetailEntry, registryEntry: AnyReactiveDetailRegistryEntry): ReactiveDetailShellViewProps {
    const liveDetail = useLiveDetailEntry(detail);
    const { setPlatformDetailTab } = usePlatformModalHost();
    const { permissions } = useAuthorization();
    const parents = liveDetail.parents ?? NO_IDS;
    const context = liveDetail.context ?? NO_IDS;
    const [activeTabId, setActiveTabId] = useState(liveDetail.tab);
    const [activeDropdownId, setActiveDropdownId] = useState(liveDetail.dropdown);

    useEffect(() => {
        if (liveDetail.tab) setActiveTabId(liveDetail.tab);
        if (liveDetail.dropdown) setActiveDropdownId(liveDetail.dropdown);
    }, [liveDetail.tab, liveDetail.dropdown]);

    const { object, initialising, isLoading } = useReactiveObject<{ id: string }>({
        id: liveDetail.id,
        entity_type: registryEntry.entityType,
        // Prefer the resolver-registered function so skipTimeout reaches useResolver (domain *Request helpers ignore the options arg).
        resolveFunction: (id, options) => {
            const registered = getResolveFunction(registryEntry.entityType);
            return registered ? registered(id, options) : registryEntry.resolveFunction(id, options);
        },
        fetchOnload: true,
        skipResolveTimeout: true
    });
    const extended = useExtendedDetail(registryEntry, object, parents, context);

    const view = useMemo<AnyView | null>(
        () => (object ? { object, extended: extended.value, parents, context, permissions } : null),
        [object, extended.value, parents, context, permissions]
    );

    let status: ReactiveDetailShellStatus = 'ready';
    if (!object) {
        // MQTT refetches keep showing the current object; only the first load shows a spinner.
        status = initialising || isLoading ? 'loading' : 'not-found';
    } else if (registryEntry.fetchExtended && !extended.loaded) {
        status = extended.failed ? 'error' : 'loading';
    }

    const tabs = view ? registryEntry.tabs.filter((tab) => !tab.visible || tab.visible(view)) : [];
    const activeTab = tabs.find((tab) => tab.id === activeTabId) ?? tabs[0];
    const activeDropdownOption =
        activeTab && 'dropdownOptions' in activeTab
            ? (activeTab.dropdownOptions.find((option) => option.id === activeDropdownId) ?? activeTab.dropdownOptions[0])
            : undefined;

    const target = { entityType: liveDetail.type, id: liveDetail.id };

    return {
        status,
        view,
        tabs,
        activeTab,
        activeDropdownOption,
        onChangeTab: (tabId) => {
            setActiveTabId(tabId);
            setPlatformDetailTab(target, tabId);
        },
        onChangeDropdown: (tabId, optionId) => {
            setActiveTabId(tabId);
            setActiveDropdownId(optionId);
            setPlatformDetailTab(target, tabId, optionId);
        }
    };
}
